import React, { useRef, useContext } from 'react';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { DropdownTrigger } from 'react-simple-dropdown';
import { useLocation } from 'react-router-dom';

// @ts-ignore
import { removeState as removeLocalStorageState } from 'store/utils/localStorage';
// @ts-ignore
import { removeState as removeSessionStorageState } from 'store/utils/sessionStorage';
// @ts-ignore
import { getUser } from 'store/services/session/selectors';
// @ts-ignore
import { logoutUser } from 'store/services/session/actions';

import { branch, compose, renderComponent } from 'recompose';

import useDropdown from 'utils/hooks/useDropdown';

import { User } from 'tstypes';

import {
  StyledDropdownContent,
  StyledDropdownContentBody,
  StyledHeaderMenuItem,
  StyledHeaderMenuItemIcon,
  StyledHeaderMenuItemLabel,
} from 'components/HeaderDropdownMenu/styled';
import { KeycloakActionsContext } from '../../hooks/useKeycloak';

import {
  SAvatar,
  SName,
  SEmail,
  SDropdownContentHeader,
  SDropdown,
} from './styled';

interface Props {
  authenticatedUser: Pick<User, 'full_name' | 'avatar_url' | 'email'>;
  logoutUser: () => void;
}

export const UserMenu = ({ authenticatedUser, logoutUser }: Props) => {
  const { pathname, search } = useLocation();
  const dropdownRef = useRef(null);
  const { hide: hideDropdown } = useDropdown(dropdownRef);
  const { logout, usingKeycloak } = useContext(KeycloakActionsContext);

  const handleLogoutUser = async () => {
    if (usingKeycloak) {
      removeLocalStorageState();
      removeSessionStorageState();
      logout({ redirectUri: process.env.KEYCLOAK_REDIRECT_URI_AFTER_LOGOUT });
    } else {
      hideDropdown();
      logoutUser();
    }
  };

  const isAtProfilePage = () => pathname.includes('/settings/profile');
  const getFromQueryParam = () => {
    const currentQueryParams = new URLSearchParams(search);

    if (!currentQueryParams.get('from')) {
      currentQueryParams.set('from', pathname);
    }

    return currentQueryParams.toString();
  };

  return (
    <SDropdown ref={dropdownRef}>
      <DropdownTrigger>
        <SAvatar
          alt={authenticatedUser.full_name}
          data-test="avatar"
          src={authenticatedUser.avatar_url}
        />
      </DropdownTrigger>
      <StyledDropdownContent>
        <SDropdownContentHeader>
          <SName>{authenticatedUser.full_name}</SName>
          <SEmail>{authenticatedUser.email}</SEmail>
        </SDropdownContentHeader>
        <StyledDropdownContentBody>
          <StyledHeaderMenuItem
            to={`/settings/profile?${getFromQueryParam()}`}
            onClick={() => (isAtProfilePage() ? hideDropdown() : null)}
          >
            <StyledHeaderMenuItemIcon className="icon-user" />
            <StyledHeaderMenuItemLabel data-test="profile-button">
              Profile
            </StyledHeaderMenuItemLabel>
          </StyledHeaderMenuItem>
          <StyledHeaderMenuItem
            as="a"
            data-test="logout"
            to=""
            onClick={handleLogoutUser}
          >
            <StyledHeaderMenuItemIcon className="icon-logout" />
            <StyledHeaderMenuItemLabel data-test="logout-button">
              Logout
            </StyledHeaderMenuItemLabel>
          </StyledHeaderMenuItem>
        </StyledDropdownContentBody>
      </StyledDropdownContent>
    </SDropdown>
  );
};

const mapStateToProps = state => ({
  authenticatedUser: getUser(state),
});

const mapDispatchToProps = {
  logoutUser,
};

const handleEmptyUser = branch(
  ({ authenticatedUser }: Pick<Props, 'authenticatedUser'>) =>
    isEmpty(authenticatedUser),
  renderComponent(() => null),
);

export default compose<Props, Function>(
  connect(mapStateToProps, mapDispatchToProps),
  handleEmptyUser,
)(UserMenu);
